/**
 * 보안 파일 저장소 목록 컴포넌트
 * 암호화된 파일 관리 UI
 */

import React, { useState, useMemo } from 'react';
import { useSecureFiles } from '../../contexts/SecureFilesContext';
import { usePin } from '../../contexts/PinContext';
import SecureFileCard from './SecureFileCard';
import SecureFileUpload from './SecureFileUpload';
import SecureFilePinModal from './SecureFilePinModal';
import SecureFilePreview from './SecureFilePreview';
import SecureFileEditModal from './SecureFileEditModal';
import { SearchBox, ActionButton, FilterLayout } from '../UI';
import {
  Upload,
  Shield,
  HardDrive,
  FileText,
  RefreshCw,
  RotateCcw
} from 'lucide-react';
import './SecureFiles.css';

function SecureFilesList() {
  const {
    files,
    loading,
    error,
    uploadProgress,
    filters,
    stats,
    loadFiles,
    uploadFile,
    downloadFile,
    deleteFile,
    updateFile,
    updateFilters,
    resetFilters,
    searchFiles
  } = useSecureFiles();
  
  const { verifyPin, checkUnlockStatus, pinSettings } = usePin();

  const [showUpload, setShowUpload] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinAction, setPinAction] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // 필터링된 파일 목록
  const filteredFiles = useMemo(() => {
    let filtered = searchFiles(searchTerm);

    // 카테고리 필터
    if (filters.category) {
      filtered = filtered.filter(file => file.category === filters.category);
    }

    // PIN 보호 필터
    if (filters.pinProtectedOnly) {
      filtered = filtered.filter(file => file.isPinProtected);
    }

    return filtered;
  }, [searchFiles, searchTerm, filters]);

  // 파일 다운로드 처리
  const handleDownload = async (file) => {
    // PIN 보호된 파일인 경우에만 PIN 확인 필요
    const needsPinConfirmation = 
      file.isPinProtected && pinSettings.hasMasterPin && !checkUnlockStatus();
    
    if (needsPinConfirmation) {
      setPinAction({ type: 'download', file });
      setShowPinModal(true);
    } else {
      try {
        await downloadFile(file.id);
      } catch (err) {
        console.error('Download error:', err);
      }
    }
  };

  // 파일 삭제 처리
  const handleDelete = async (file) => {
    // PIN 보호된 파일인 경우에만 PIN 확인 필요
    const needsPinConfirmation = 
      file.isPinProtected && pinSettings.hasMasterPin && !checkUnlockStatus();
    
    if (needsPinConfirmation) {
      setPinAction({ type: 'delete', file });
      setShowPinModal(true);
    } else {
      if (window.confirm(`정말 "${file.originalName}" 파일을 삭제하시겠습니까?`)) {
        try {
          await deleteFile(file.id);
        } catch (err) {
          console.error('Delete error:', err);
        }
      }
    }
  };

  // 파일 미리보기 처리
  const handlePreview = (file) => {
    // PIN 보호된 파일인 경우에만 PIN 확인 필요
    const needsPinConfirmation = 
      file.isPinProtected && pinSettings.hasMasterPin && !checkUnlockStatus();
    
    if (needsPinConfirmation) {
      setPinAction({ type: 'preview', file });
      setShowPinModal(true);
    } else {
      setPreviewFile(file);
    }
  };

  // PIN 확인 후 작업 수행
  const handlePinSubmit = async (pin) => {
    if (!pinAction) return;

    try {
      // PIN 검증은 새로운 보안 아키텍처에서 Service 레벨에서 처리
      // 여기서는 단순히 PIN을 전달만 함
      const effectivePin = pin;
      
      switch (pinAction.type) {
        case 'download':
          await downloadFile(pinAction.file.id, effectivePin);
          break;

        case 'delete':
          if (window.confirm(`정말 "${pinAction.file.originalName}" 파일을 삭제하시겠습니까?`)) {
            await deleteFile(pinAction.file.id, effectivePin);
          }
          break;

        case 'preview':
          setPreviewFile(pinAction.file);
          break;

        default:
          break;
      }
    } catch (err) {
      // PIN 검증 실패는 사용자 입력 오류이므로 에러 로그 생략
      if (!err.message?.includes('PIN')) {
        console.error('PIN action error:', err);
      }
      // PIN이 틀렸을 경우 에러 메시지 표시
      if (err.message?.includes('PIN')) {
        alert('잘못된 PIN입니다.');
      }
    } finally {
      setPinAction(null);
      setShowPinModal(false);
    }
  };

  // 파일 크기 포맷
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // 카테고리 목록
  const categories = [
    { value: '', label: '전체', icon: FileText },
    { value: 'document', label: '문서', icon: FileText },
    { value: 'image', label: '이미지', icon: FileText },
    { value: 'finance', label: '금융', icon: FileText },
    { value: 'personal', label: '개인', icon: FileText },
    { value: 'work', label: '업무', icon: FileText },
    { value: 'other', label: '기타', icon: FileText }
  ];

  return (
    <div className="secure-files-container">
      {/* 헤더 액션 영역 */}
      <div className="secure-files-header">
        <div className="header-info">
          <span className="files-count">전체 {stats.total}개</span>
        </div>

        <div className="header-actions">
          <ActionButton
            variant="primary"
            size="md"
            icon={Upload}
            onClick={() => setShowUpload(true)}
            disabled={loading}
            title="파일 업로드"
          >
            파일 업로드
          </ActionButton>
          <ActionButton
            variant="secondary"
            size="icon-only"
            icon={RefreshCw}
            onClick={loadFiles}
            disabled={loading}
            title="새로고침"
          />
        </div>
      </div>

      {/* 검색 및 필터 */}
      <FilterLayout
        searchSlot={
          <SearchBox
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="파일명, 설명, 태그로 검색..."
          />
        }
        filterSlot={
          <>
            <select
              className="filter-select"
              value={filters.category || ''}
              onChange={(e) => updateFilters({ category: e.target.value })}
            >
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>

            <ActionButton
              variant="secondary"
              size="icon-only"
              icon={Shield}
              onClick={() => updateFilters({ pinProtectedOnly: !filters.pinProtectedOnly })}
              title="PIN 보호 파일만"
              className={filters.pinProtectedOnly ? 'active' : ''}
            />

            <ActionButton
              variant="ghost"
              size="icon-only"
              icon={RotateCcw}
              onClick={resetFilters}
              title="필터 초기화"
            />
          </>
        }
        className="secure-files-filters"
      />

      {/* 통계 */}
      <div className="secure-files-stats">
        <div className="stat-item">
          <HardDrive size={14} />
          <span>전체 용량: {formatFileSize(stats.totalSize)}</span>
        </div>
        <div className="stat-item">
          <Shield size={14} />
          <span>PIN 보호: {stats.pinProtected}개</span>
        </div>
        <div className="stat-item">
          <FileText size={14} />
          <span>공유중: {stats.shared}개</span>
        </div>
      </div>

      {/* 업로드 진행률 */}
      {uploadProgress > 0 && (
        <div className="upload-progress">
          <div className="progress-bar">
            <div 
              className="progress-fill"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
          <span className="progress-text">업로드 중... {uploadProgress}%</span>
        </div>
      )}

      {/* 파일 목록 */}
      {loading && !files.length ? (
        <div className="secure-files-loading">
          파일을 불러오는 중...
        </div>
      ) : error ? (
        <div className="secure-files-error">
          {error}
        </div>
      ) : filteredFiles.length === 0 ? (
        <div className="secure-files-empty">
          {searchTerm || filters.category || filters.pinProtectedOnly ? (
            <>
              <Filter size={48} />
              <h3>검색 결과가 없습니다</h3>
              <p>다른 검색어나 필터를 시도해보세요.</p>
            </>
          ) : (
            <>
              <Upload size={48} />
              <h3>보관된 파일이 없습니다</h3>
              <p>중요한 파일을 안전하게 보관해보세요.</p>
              <ActionButton
                variant="primary"
                size="md"
                icon={Upload}
                onClick={() => setShowUpload(true)}
              >
                첫 파일 업로드
              </ActionButton>
            </>
          )}
        </div>
      ) : (
        <div className="secure-files-grid">
          {filteredFiles.map(file => (
            <SecureFileCard
              key={file.id}
              file={file}
              onDownload={() => handleDownload(file)}
              onDelete={() => handleDelete(file)}
              onPreview={() => handlePreview(file)}
              onEdit={() => setSelectedFile(file)}
            />
          ))}
        </div>
      )}

      {/* 파일 업로드 모달 */}
      {showUpload && (
        <SecureFileUpload
          isOpen={showUpload}
          onClose={() => setShowUpload(false)}
          onUpload={async (file, metadata) => {
            await uploadFile(file, metadata);
            setShowUpload(false);
          }}
        />
      )}

      {/* 파일 미리보기 모달 */}
      {previewFile && (
        <SecureFilePreview
          file={previewFile}
          isOpen={!!previewFile}
          onClose={() => setPreviewFile(null)}
        />
      )}

      {/* PIN 입력 모달 */}
      {showPinModal && (
        <SecureFilePinModal
          isOpen={showPinModal}
          onClose={() => {
            setShowPinModal(false);
            setPinAction(null);
          }}
          onSubmit={handlePinSubmit}
          mode="verify"
          title={
            pinAction?.type === 'download' ? 'PIN을 입력하여 다운로드' :
            pinAction?.type === 'delete' ? 'PIN을 입력하여 삭제' :
            'PIN을 입력하여 미리보기'
          }
        />
      )}

      {/* 파일 편집 모달 */}
      {selectedFile && (
        <SecureFileEditModal
          file={selectedFile}
          isOpen={!!selectedFile}
          onClose={() => setSelectedFile(null)}
          onUpdate={async (fileId, updates, pin) => {
            await updateFile(fileId, updates, pin);
            setSelectedFile(null);
          }}
        />
      )}
    </div>
  );
}

export default SecureFilesList;